import { useEffect, useRef, useState, useCallback } from 'react';
import { GIBBERISH_WINDOW } from '../config/constants';
import { median, mad, robustZ } from '../engine/stats';
import { Baselines } from '../types';

interface KeyboardState {
  keyCount: number;
  keyboardZ: number;
  isGibberish: boolean;
  lastInputTime: number;
}

interface KeyInterval {
  t: number;
  v: number; // ms interval
}

/**
 * Keyboard sensor: tracks key intervals, Z-score, and gibberish detection.
 */
export function useKeyboardSensor(baselines: Baselines) {
  const [state, setState] = useState<KeyboardState>({
    keyCount: 0,
    keyboardZ: 0,
    isGibberish: false,
    lastInputTime: 0,
  });

  const keyCountRef = useRef(0);
  const lastKeyTimeRef = useRef(0);
  const intervalsRef = useRef<KeyInterval[]>([]);
  const recentCharsRef = useRef<string[]>([]);
  const lastInputTimeRef = useRef(0);

  // Calibration
  const calibIntervalsRef = useRef<number[]>([]);
  const isCalibRef = useRef(false);

  const startCalibCollection = useCallback(() => {
    calibIntervalsRef.current = [];
    isCalibRef.current = true;
  }, []);

  const stopCalibCollection = useCallback((): number[] => {
    isCalibRef.current = false;
    return calibIntervalsRef.current;
  }, []);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    const now = Date.now();
    keyCountRef.current++;
    lastInputTimeRef.current = now;

    if (e.key.length === 1) {
      recentCharsRef.current.push(e.key);
      if (recentCharsRef.current.length > 50) recentCharsRef.current.shift();
    }

    if (lastKeyTimeRef.current > 0) {
      const interval = now - lastKeyTimeRef.current;
      if (interval > 30 && interval < 3000) {
        intervalsRef.current.push({ t: now, v: interval });
        if (intervalsRef.current.length > 30) intervalsRef.current.shift();
        if (isCalibRef.current) calibIntervalsRef.current.push(interval);
      }
    }
    lastKeyTimeRef.current = now;

    // Compute Z-score
    const now2 = Date.now();
    const recent = intervalsRef.current.filter(k => now2 - k.t < 5000);
    let keyZ = 0;
    if (recent.length >= 2 && baselines.keyInterval) {
      const currentMedian = median(recent.map(k => k.v));
      keyZ = robustZ(currentMedian, baselines.keyInterval.med, baselines.keyInterval.mad);
    }

    // Gibberish detection
    const chars = recentCharsRef.current.slice(-GIBBERISH_WINDOW);
    let gibberish = false;
    if (chars.length >= GIBBERISH_WINDOW) {
      const text = chars.join('');
      const jamoRegex = /[\u3131-\u3163\u314F-\u3163]/g;
      const jamoCount = (text.match(jamoRegex) || []).length;
      const jamoRatio = jamoCount / text.length;
      const repeatRegex = /(.)\1{3,}/g;
      const hasLongRepeat = repeatRegex.test(text);
      gibberish = jamoRatio > 0.6 || hasLongRepeat;
    }

    setState({
      keyCount: keyCountRef.current,
      keyboardZ: keyZ,
      isGibberish: gibberish,
      lastInputTime: lastInputTimeRef.current,
    });
  }, [baselines.keyInterval]);

  // Expose ref values for external access
  const getIntervals = useCallback(() => intervalsRef.current, []);
  const getLastInputTime = useCallback(() => lastInputTimeRef.current, []);

  return {
    ...state,
    handleKeyDown,
    startCalibCollection,
    stopCalibCollection,
    getIntervals,
    getLastInputTime,
  };
}
